import { queryOptions, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { POLL_MS, apiGet, apiPost, useCommandMutation, withQuery } from "@amber/ui";
import {
  addCopyPreviewSchema,
  copyCandidatesSchema,
  evidenceRecordSchema,
  makePrimaryPreviewSchema,
  mergePreviewSchema,
  optionalCheckPreviewSchema,
  optionalChecksResponseSchema,
  policiesResponseSchema,
  policyImpactSchema,
  recordDetailsPreviewSchema,
  separateCopyPreviewSchema,
  splitPreviewSchema,
  translationResponseSchema,
  type OptionalCheckEdit,
  type PolicyDimensionsInput,
} from "../contracts/index";
import { evidenceKeys } from "./keys";

type Csrf = () => string | null;

export const recordQuery = (itemId: string, version: number | undefined, communication: number | undefined) =>
  queryOptions({
    queryKey: evidenceKeys.record(itemId, version, communication),
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(`/api/evidence/items/${encodeURIComponent(itemId)}`, {
          version: version === undefined ? undefined : String(version),
          communication: communication === undefined ? undefined : String(communication),
        }),
        evidenceRecordSchema,
        signal,
      ),
    refetchInterval: POLL_MS,
  });

export const translationQuery = (itemId: string, language: "en" | "de", version: number | undefined) =>
  queryOptions({
    queryKey: evidenceKeys.translation(itemId, language, version),
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(`/api/evidence/items/${encodeURIComponent(itemId)}/translation`, {
          language,
          version: version === undefined ? undefined : String(version),
        }),
        translationResponseSchema,
        signal,
      ),
  });

export function useRequestTranslation(itemId: string, version: number | undefined) {
  const client = useQueryClient();
  return useCommandMutation({
    run: (csrf, language: "en" | "de") =>
      apiPost(
        `/api/evidence/items/${encodeURIComponent(itemId)}/translation`,
        { language, ...(version === undefined ? {} : { version }) },
        translationResponseSchema,
        csrf,
      ),
    invalidate: [],
    onSuccess: (data, language) => {
      client.setQueryData(translationQuery(itemId, language, version).queryKey, data);
    },
  });
}

export function useEvidenceCommand<S extends z.ZodType>(itemId: string, schema: S) {
  return useCommandMutation({
    run: (csrf, { path, body }: { path: string; body: unknown }) => apiPost(path, body, schema, csrf),
    invalidate: [evidenceKeys.all, evidenceKeys.recordsOf(itemId)],
  });
}

export const copyCandidatesQuery = (itemId: string) =>
  queryOptions({
    queryKey: evidenceKeys.copyCandidates(itemId),
    queryFn: () => apiGet(`/api/evidence/items/${itemId}/copy-candidates`, copyCandidatesSchema),
  });

interface CopyPreview<S extends z.ZodType> {
  readonly key: unknown[];
  readonly path: string;
  readonly body: unknown;
  readonly schema: S;
}

const copyPreviewQuery = <S extends z.ZodType>(
  { key, path, body, schema }: CopyPreview<S>,
  enabled: boolean,
  csrf: Csrf,
) =>
  queryOptions({
    queryKey: evidenceKeys.preview(...key),
    queryFn: () => apiPost(path, body, schema, csrf()),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

export const addCopyPreviewQuery = (itemId: string, key: unknown[], body: unknown, enabled: boolean, csrf: Csrf) =>
  copyPreviewQuery(
    {
      key: ["copy", itemId, ...key],
      path: `/api/evidence/items/${itemId}/copies/preview`,
      body,
      schema: addCopyPreviewSchema,
    },
    enabled,
    csrf,
  );

export const makePrimaryPreviewQuery = (itemId: string, copyId: string, csrf: Csrf) =>
  copyPreviewQuery(
    {
      key: ["primary", copyId],
      path: `/api/evidence/items/${itemId}/primary/preview`,
      body: { copy_id: copyId, reason: "-" },
      schema: makePrimaryPreviewSchema,
    },
    true,
    csrf,
  );

export const separateCopyPreviewQuery = (itemId: string, copyId: string, csrf: Csrf) =>
  copyPreviewQuery(
    {
      key: ["separate", copyId],
      path: `/api/evidence/items/${itemId}/separate/preview`,
      body: { copy_id: copyId, reason: "-" },
      schema: separateCopyPreviewSchema,
    },
    true,
    csrf,
  );

export const detailsPreviewQuery = (
  communicationId: string,
  body: Record<string, unknown>,
  enabled: boolean,
  csrf: Csrf,
) =>
  queryOptions({
    queryKey: evidenceKeys.preview("details", JSON.stringify({ ...body, reason: null, resolves: null })),
    queryFn: () =>
      apiPost(
        `/api/evidence/communications/${communicationId}/details/preview`,
        body,
        recordDetailsPreviewSchema,
        csrf(),
      ),
    enabled,
    retry: false,
    gcTime: 0,
  });

export const splitPreviewQuery = (
  communicationId: string,
  unitId: string | null,
  body: unknown,
  enabled: boolean,
  csrf: Csrf,
) =>
  queryOptions({
    queryKey: evidenceKeys.preview("split", unitId),
    queryFn: () =>
      apiPost(`/api/evidence/communications/${communicationId}/split/preview`, body, splitPreviewSchema, csrf()),
    enabled,
    retry: false,
    gcTime: 0,
  });

export const mergePreviewQuery = (communicationId: string, body: unknown, enabled: boolean, csrf: Csrf) =>
  queryOptions({
    queryKey: evidenceKeys.preview("merge", communicationId),
    queryFn: () =>
      apiPost(`/api/evidence/communications/${communicationId}/merge/preview`, body, mergePreviewSchema, csrf()),
    enabled,
    retry: false,
    gcTime: 0,
  });

export const policiesQuery = queryOptions({
  queryKey: evidenceKeys.policies,
  queryFn: ({ signal }) => apiGet("/api/evidence/policies", policiesResponseSchema, signal),
});

export const policyPreviewQuery = (dimensions: PolicyDimensionsInput, enabled: boolean, csrf: Csrf) =>
  queryOptions({
    queryKey: evidenceKeys.policyPreview(dimensions),
    queryFn: () => apiPost("/api/evidence/policies/preview", { dimensions }, policyImpactSchema, csrf()),
    enabled,
  });

export const optionalChecksQuery = queryOptions({
  queryKey: evidenceKeys.optionalChecks,
  queryFn: ({ signal }) => apiGet("/api/evidence/optional-checks", optionalChecksResponseSchema, signal),
});

export const optionalCheckPreviewQuery = (
  kind: string,
  values: Omit<OptionalCheckEdit, "base_revision" | "reason"> | null,
  baseRevision: number,
  csrf: Csrf,
) =>
  queryOptions({
    queryKey: evidenceKeys.optionalCheckPreview(kind, values),
    queryFn: () =>
      apiPost(
        `/api/evidence/optional-checks/${kind}/preview`,
        { ...values, base_revision: baseRevision, reason: "preview" },
        optionalCheckPreviewSchema,
        csrf(),
      ),
    enabled: values !== null,
  });
